import { TextField as MuiTextField } from '@mui/material';
import { MuiTextFieldProps } from '@mui/x-date-pickers/internals';
import { FieldPath, FieldValues } from 'react-hook-form';
import {
    NumericFormat, NumericFormatProps, PatternFormat, PatternFormatProps
} from 'react-number-format';

import { ControlledTextFieldProps, useControlledTextFieldProps } from '.';


export type MaskProps = (NumericFormatProps<MuiTextFieldProps> | PatternFormatProps<MuiTextFieldProps>) & { valueType?: 'number' | 'string' | 'formatted' };

export type TextFieldProps<
    TFieldValues extends FieldValues,
    TName extends FieldPath<TFieldValues>
> = ControlledTextFieldProps<TFieldValues, TName> & { maskProps?: MaskProps };

/**
 * Renders a react-hook-form controlled mui TextField component.
 *
 * @example
 * ```ts
 * import { FormProvider, useForm } from 'react-hook-form';
 *
 * const formMethods = useForm();
 *
 * <FormProvider {...formMethods}>
 *     <TextField name="name" />
 * </FormProvider>
 * ```
 *
 * @param props - The props for the Controller and the underlying TextField
 * @constructor
 */
export function TextField<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ maskProps, ...props }: TextFieldProps<TFieldValues, TName>) {
    const textFieldProps = useControlledTextFieldProps<TFieldValues, TName>(props);

    const { valueType, type, ...otherMaskProps } = maskProps || {};
    const { onChange, ...otherTextFieldProps } = textFieldProps;

    const formatProps: (NumericFormatProps<MuiTextFieldProps> | PatternFormatProps<MuiTextFieldProps>) = {
        ...otherTextFieldProps,
        ...otherMaskProps,
        type,
        customInput: MuiTextField,
        onValueChange: (
            /**
             * When a user clears an input, floatValue will be undefined. If we call onChange with undefined,
             * it will reset the field to its default value. We can avoid this by defaulting floatValue to null,
             * and calling onChange with null instead of undefined when the input is cleared.
             */
            { value, floatValue = null, formattedValue }
        ) => onChange(
            valueType === 'string'
                ? value
                : valueType === 'formatted'
                    ? formattedValue
                    : floatValue
        )
    };

    return !maskProps
        ? <MuiTextField {...textFieldProps} />
        : isPatternFormatProps(formatProps)
            ? <PatternFormat {...formatProps} />
            : <NumericFormat {...formatProps} />;
}

function isPatternFormatProps(
    props: NumericFormatProps<MuiTextFieldProps> | PatternFormatProps<MuiTextFieldProps>
): props is PatternFormatProps<MuiTextFieldProps> {
    return props?.hasOwnProperty('format');
}
